import * as Common from '@tfo/common'
import { ReportNotificationModalProps } from '@tfo/common/src/components/report/components/report-notification-modal/ReportNotificationModal'
import { DepersonalizedOrderInfo, IReportStore } from '@tfo/common/src/interfaces'
import { BaseCheckpointModel } from '@tfo/common/src/models/CheckpointModels/BaseCheckpointModel'
import { action, computed, observable } from 'mobx'
import $http from '../utils/$http'

export interface ReportStoreSectionProps {
	id: string
	label: string
}

export interface ReportPackageAddon {
	addonId: number
	addonName: string
	partnerId: number
	partnerName: string
}

export class ReportStore implements IReportStore {
	private hasCollectedCheckpoints: boolean = false

	@observable loading: boolean = false
	@observable section: string
	@observable showInfoModal: boolean
	@observable infoModalData: ReportNotificationModalProps
	@observable initialDataLoaded: boolean = false

	@observable order: Common.models.IOrderClient

	@observable nameAndNationalIdCheckpoint: Common.models.PersonaliaModels.NameAndNationalIdCheckpoint
	@observable nameChangeCheckpoint: Common.models.PersonaliaModels.NameChangeCheckpoint
	@observable childrenCheckpoint: Common.models.PersonaliaModels.ChildrenCheckpoint
	@observable driversLicenseCheckpoint: Common.models.PersonaliaModels.DriversLicenseCheckpoint
	@observable residentialAddressCheckpoint: Common.models.PersonaliaModels.ResidentialAddressCheckpoint
	@observable residentialAddressHistoryCheckpoint: Common.models.PersonaliaModels.ResidentialAddressHistoryCheckpoint
	@observable maritalStatusCheckpoint: Common.models.PersonaliaModels.MaritalStatusCheckpoint
	@observable migrationCheckpoint: Common.models.PersonaliaModels.MigrationCheckpoint
	@observable militaryServiceCheckpoint: Common.models.PersonaliaModels.MilitaryServiceCheckpoint
	@observable cohabitantsCheckpoint: Common.models.PersonaliaModels.CohabitantsCheckpoint

	@observable personalDebtCheckpoint: Common.models.FinancialModels.PersonalDebtCheckpoint
	@observable studentDebtCheckpoint: Common.models.FinancialModels.StudentDebtCheckpoint
	@observable taxableIncomeCheckpoint: Common.models.FinancialModels.TaxableIncomeCheckpoint
	@observable vehicleOwnershipCheckpoint: Common.models.FinancialModels.VehicleOwnershipCheckpoint
	@observable paymentDefaultCheckpoint: Common.models.FinancialModels.PaymentDefaultCheckpoint
	@observable corporateEngagementCheckpoint: Common.models.FinancialModels.CorporateEngagementCheckpoint
	@observable extendedCorporateEngagementMappingCheckpoint: Common.models.FinancialModels.ExtendedCorporateEngagementMappingCheckpoint
	@observable seizureAttemptsCheckpoint: Common.models.FinancialModels.SeizureAttemptsCheckpoint
	@observable personalBankruptcyCheckpoint: Common.models.FinancialModels.PersonalBankruptcyCheckpoint
	@observable propertyOwnershipCheckpoint: Common.models.FinancialModels.PropertyOwnershipCheckpoint
	@observable banOnBusinessAcitvityCheckpoint: Common.models.FinancialModels.BanOnBusinessActivtyCheckpoint
	@observable extendedFinancialMappingActivtyCheckpoint: Common.models.FinancialModels.ExtendedFinancialMappingActivtyCheckpoint
	@observable publicTrusteeshipCheckpoint: Common.models.FinancialModels.PublicTrusteeshipCheckpoint
	@observable partnerBackgroundCheckCheckpoint: Common.models.FinancialModels.PartnerBackgroundCheckCheckpoint
	@observable soleproprietorshipCheckpoint: Common.models.FinancialModels.SoleProprietorshipCheckpoint
	@observable representationProhobition: Common.models.FinancialModels.RepresentationProhibitionCheckpoint
	@observable beneficialOwnerCheckpoint: Common.models.FinancialModels.BeneficialOwnerCheckpoint

	@observable districtCourtsCheckpoint: Common.models.LegalModels.CourtCheckpoint
	@observable courtsofAppealCheckpoint: Common.models.LegalModels.CourtCheckpoint
	@observable administrativeCourtsCheckpoint: Common.models.LegalModels.CourtCheckpoint
	@observable sanctionsListCheckpoint: Common.models.LegalModels.SanctionsListCheckpoint
	@observable pepListCheckpoint: Common.models.LegalModels.PEPCheckpoint

	@observable employmentCheckpoint: Common.models.ResumeModels.EmploymentCheckpoint
	@observable currentEmploymentCheckpoint: Common.models.ResumeModels.EmploymentCheckpoint
	@observable educationCheckpoint: Common.models.ResumeModels.EducationCheckpoint
	@observable highestEducationCheckpoint: Common.models.ResumeModels.EducationCheckpoint
	@observable referencesCheckpoint: Common.models.ResumeModels.ReferencesCheckpoint
	@observable otherMeritsCheckpoint: Common.models.ResumeModels.OtherMeritsCheckpoint

	@observable internetAndMediaCheckpoint: Common.models.MediaModels.InternetAndMediaCheckpoint

	@observable archivedCheckpoints: BaseCheckpointModel[]

	@observable activeReportPackages: Common.models.ReportPackage[]
	@observable company: Common.models.ICompany
	@observable reportPackageName: string
	@observable isIDD: boolean
	@observable depersonalizedOrderInformation: DepersonalizedOrderInfo | null = null
	@observable partnerAddonJobsResults: Common.models.PartnerAddonJobsResults
	@observable shouldShowReportGuideAutomatically: boolean

	network: Common.services.Network
	userStore: Common.interfaces.UserStore

	constructor({ network, userStore }) {
		this.userStore = userStore
		this.network = network
	}

	public async getShouldAutomaticallyShowGuide(): Promise<boolean> {
		return await $http.get<boolean>(`/read-report/should-show-guide`).then((data) => {
			return data;
		}).catch((e) => {
			return false
		})
	}

	public async getReportData(orderId: number): Promise<any> {
		const orderData = await $http.get<any>(`/order/${orderId}`)
		const checkpoints = await $http.get<any>(`/order/${orderId}/checkpoints`)
		if (orderData.candidateId !== 0) {
			orderData.candidate = await $http.get<any>(`/user/individual-information/${orderData.candidateId}`)
		}

		const creator = await $http.get<any>(`/user/customer/${orderData.creatorId}`)

		if (orderData.readReceipt && orderData.readReceipt.length > 0) {
			const latestPerUser = Object.values(
				(orderData.readReceipt as { userId: number; time: string }[]).reduce((acc, receipt) => {
					if (!acc[receipt.userId] || receipt.time > acc[receipt.userId].time) {
						acc[receipt.userId] = receipt
					}
					return acc
				}, {} as Record<number, { userId: number; time: string }>)
			)
			orderData.readReceipt = await Promise.all(
				latestPerUser.map(async (receipt) => {
					const recipientData = await $http.get<any>(`/user/customer/${receipt.userId}`)
					return { userId: receipt.userId, userNameAndSurName: `${recipientData?.firstName} ${recipientData?.lastName}`, time: receipt.time }
				})
			)
		}

		if (orderData.orderRecipients && orderData.orderRecipients.length > 0) {
			orderData.orderRecipientUser = await Promise.all(
				orderData.orderRecipients.map(async (userId: number) => {
					const recipientData = await $http.get<any>(`/user/customer/${userId}`)
					return { userId, userNameAndSurName: `${recipientData?.firstName} ${recipientData?.lastName}` }
				})
			)
		}

		const company = await $http.get<any>(`/company/${orderData.companyId}?subsidiaries=false`)

		const reportPackageName = orderData.reportPackage.customName

		orderData.checkpointInfo = orderData.checkpointTypes.reduce((accumulator, value) => {
			return { ...accumulator, [`service.checkpoints.${value}`]: `readreport.informationsource.${value}` }
		}, {}) as any

		orderData.creatorName = `${creator?.firstName} ${creator?.lastName}`

		const addonJobsResults = await $http.get<Common.models.PartnerAddonJobsResults>(`/partneraddons/jobs/${orderId}/result`)
		this.partnerAddonJobsResults = addonJobsResults

		return { checkpoints, orderData, company, reportPackageName }
	}

	sortObjectByKeys = (unordered: Record<string, unknown>) => {
		const ordered = {}
		Object.keys(unordered)
			.sort()
			.forEach(key => {
				ordered[key] = unordered[key]
			})
		return ordered
	}

	@action async fetchOrder(orderId: number) {
		// reset before fetching new data to avoid showing wrong information if is not fetched, 
		// ex when user is not authorized to see the report or it doesn't exist.
		this.initialDataLoaded = false
		this.order = null
		this.company = null
		this.reportPackageName = null
		this.partnerAddonJobsResults = null
		this.depersonalizedOrderInformation = null
		this.resetCheckpoints()

		this.shouldShowReportGuideAutomatically = await this.getShouldAutomaticallyShowGuide()

		try {
			const {
				checkpoints,
				orderData,
				company,
				reportPackageName
			} = await this.getReportData(orderId)

			this.order = orderData
			this.company = company
			this.reportPackageName = reportPackageName

			if (checkpoints) {
				Object.keys(checkpoints).forEach(async checkpointCatergory => {
					checkpoints[checkpointCatergory].forEach(async checkpoint => {
						if (this.isArchivedCheckpoint(checkpoint)) {
							this.archivedCheckpoints.push(new Common.models.ArchivedModels.ArchivedCheckpoint(checkpoint))
						}
						else {
							await this.assignToStoreProperties(this.order, checkpoint)
						}
					})
				})
				this.hasCollectedCheckpoints = true
			}

			if (orderData.state === Common.models.State.Deleted) {
				$http.get<any>(`/order/depersonalization/${orderData.id}`).then((response) => {
					this.depersonalizedOrderInformation = response.orderDepersonalization
				}).catch(() => {
					this.depersonalizedOrderInformation = null
				})

			} else {
				this.depersonalizedOrderInformation = null
			}
		} catch (error) {
			//if it's not an authorization error, rethrow it.
			if (!error.toString().indexOf("40")) {
				throw error
			}

			console.log("User is not authorized")
		}

		this.initialDataLoaded = true
	}

	@action resetCheckpoints() {
		this.hasCollectedCheckpoints = false
		this.archivedCheckpoints = []
		this.nameAndNationalIdCheckpoint = undefined
		this.nameChangeCheckpoint = undefined
		this.driversLicenseCheckpoint = undefined
		this.residentialAddressCheckpoint = undefined
		this.residentialAddressHistoryCheckpoint = undefined
		this.migrationCheckpoint = undefined
		this.personalDebtCheckpoint = undefined
		this.studentDebtCheckpoint = undefined
		this.taxableIncomeCheckpoint = undefined
		this.vehicleOwnershipCheckpoint = undefined
		this.paymentDefaultCheckpoint = undefined
		this.corporateEngagementCheckpoint = undefined
		this.seizureAttemptsCheckpoint = undefined
		this.personalBankruptcyCheckpoint = undefined
		this.propertyOwnershipCheckpoint = undefined
		this.employmentCheckpoint = undefined
		this.currentEmploymentCheckpoint = undefined
		this.educationCheckpoint = undefined
		this.referencesCheckpoint = undefined
		this.otherMeritsCheckpoint = undefined
		this.cohabitantsCheckpoint = undefined
		this.extendedCorporateEngagementMappingCheckpoint = undefined
		this.banOnBusinessAcitvityCheckpoint = undefined
		this.extendedFinancialMappingActivtyCheckpoint = undefined
		this.publicTrusteeshipCheckpoint = undefined
		this.partnerBackgroundCheckCheckpoint = undefined
		this.soleproprietorshipCheckpoint = undefined
		this.representationProhobition = undefined
		this.beneficialOwnerCheckpoint = undefined
		this.internetAndMediaCheckpoint = undefined
		this.highestEducationCheckpoint = undefined
		this.childrenCheckpoint = undefined
		this.maritalStatusCheckpoint = undefined
		this.militaryServiceCheckpoint = undefined
		this.districtCourtsCheckpoint = undefined
		this.courtsofAppealCheckpoint = undefined
		this.administrativeCourtsCheckpoint = undefined
		this.sanctionsListCheckpoint = undefined
		this.pepListCheckpoint = undefined
	}

	@action public async updateData(order: Common.models.IOrderClient) {
		this.loading = true
		this.fetchOrder(order.id)
		this.loading = false
	}

	@action public async updateDataByOrderId(orderId: number) {
		this.loading = true
		this.fetchOrder(orderId)
		this.loading = false
	}

	private isArchivedCheckpoint(checkpoint: any): boolean {
		return checkpoint.archived;
	}

	private assignToStoreProperties(order: Common.models.IOrderClient, checkpoint: any) {
		if (checkpoint === null) return null

		switch (checkpoint.type) {
			case 'NameAndNationalId':
				this.nameAndNationalIdCheckpoint = new Common.models.PersonaliaModels.NameAndNationalIdCheckpoint(
					checkpoint
				)
				break
			case 'Children':
				this.childrenCheckpoint = new Common.models.PersonaliaModels.ChildrenCheckpoint(
					checkpoint
				)
				break
			case 'ResidentialAddresses':
				this.residentialAddressCheckpoint = new Common.models.PersonaliaModels.ResidentialAddressCheckpoint(
					checkpoint
				)
				break
			case 'Cohabitants':
				this.cohabitantsCheckpoint = new Common.models.PersonaliaModels.CohabitantsCheckpoint(
					checkpoint
				)
				break
			case 'PersonalDebt':
				this.personalDebtCheckpoint = new Common.models.FinancialModels.PersonalDebtCheckpoint(
					checkpoint
				)
				break
			case 'StudentDebt':
				this.studentDebtCheckpoint = new Common.models.FinancialModels.StudentDebtCheckpoint(
					checkpoint
				)
				break
			case 'PaymentDefault':
				this.paymentDefaultCheckpoint = new Common.models.FinancialModels.PaymentDefaultCheckpoint(
					checkpoint
				)
				break
			case 'SeizureAttempts':
				this.seizureAttemptsCheckpoint = new Common.models.FinancialModels.SeizureAttemptsCheckpoint(
					checkpoint
				)
				break
			case 'PersonalBankruptcy':
				this.personalBankruptcyCheckpoint = new Common.models.FinancialModels.PersonalBankruptcyCheckpoint(
					checkpoint
				)
				break
			case 'DistrictCourts':
				this.districtCourtsCheckpoint = new Common.models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'CourtsOfAppeal':
				this.courtsofAppealCheckpoint = new Common.models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'AdministrativeCourts':
				this.administrativeCourtsCheckpoint = new Common.models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'SanctionsList':
				this.sanctionsListCheckpoint = new Common.models.LegalModels.SanctionsListCheckpoint(
					checkpoint
				)
				break
			case 'PoliticallyExposedPerson':
				this.pepListCheckpoint = new Common.models.LegalModels.PEPCheckpoint(checkpoint)
				break
			case 'DriversLicense':
				this.driversLicenseCheckpoint = new Common.models.PersonaliaModels.DriversLicenseCheckpoint(
					checkpoint
				)
				break
			case 'MaritalStatus':
				this.maritalStatusCheckpoint = new Common.models.PersonaliaModels.MaritalStatusCheckpoint(
					checkpoint
				)
				break
			case 'Migration':
				this.migrationCheckpoint = new Common.models.PersonaliaModels.MigrationCheckpoint(
					checkpoint
				)
				break
			case 'MilitaryService':
				this.militaryServiceCheckpoint = new Common.models.PersonaliaModels.MilitaryServiceCheckpoint(
					checkpoint
				)
				break
			case 'VehicleOwnership':
				this.vehicleOwnershipCheckpoint = new Common.models.FinancialModels.VehicleOwnershipCheckpoint(
					checkpoint
				)
				break
			case 'CorporateEngagement':
				this.corporateEngagementCheckpoint = new Common.models.FinancialModels.CorporateEngagementCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.ExtendedCorporateEngagementMapping:
				this.extendedCorporateEngagementMappingCheckpoint = new Common.models.FinancialModels.ExtendedCorporateEngagementMappingCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.BanOnBusinessActivity:
				this.banOnBusinessAcitvityCheckpoint = new Common.models.FinancialModels.BanOnBusinessActivtyCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.ExtendedFinancialMapping:
				this.extendedFinancialMappingActivtyCheckpoint = new Common.models.FinancialModels.ExtendedFinancialMappingActivtyCheckpoint(
					checkpoint
				)
				break
			case 'NameChange':
				this.nameChangeCheckpoint = new Common.models.PersonaliaModels.NameChangeCheckpoint(
					checkpoint
				)
				break
			case 'HistoryResidentialAddresses':
				this.residentialAddressHistoryCheckpoint = new Common.models.PersonaliaModels.ResidentialAddressHistoryCheckpoint(
					checkpoint
				)
				break
			case 'TaxableIncomeTwoYears':
			case 'TaxableIncomeThreeYears':
			case 'TaxableIncomeFiveYears':
				this.taxableIncomeCheckpoint = new Common.models.FinancialModels.TaxableIncomeCheckpoint(
					checkpoint
				)
				break
			case 'TaxableIncomeTenYears':
				this.taxableIncomeCheckpoint = new Common.models.FinancialModels.TaxableIncomeCheckpoint(
					checkpoint
				)
				break
			case 'PropertyOwnership':
				this.propertyOwnershipCheckpoint = new Common.models.FinancialModels.PropertyOwnershipCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.PublicTrusteeship:
				this.publicTrusteeshipCheckpoint = new Common.models.FinancialModels.PublicTrusteeshipCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.PartnerBackgroundCheck:
				this.partnerBackgroundCheckCheckpoint = new Common.models.FinancialModels.PartnerBackgroundCheckCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.RepresentationProhibition:
				this.representationProhobition = new Common.models.FinancialModels.PublicTrusteeshipCheckpoint(
					checkpoint
				)
				break
			case Common.models.CheckpointType.BeneficialOwner:
				this.beneficialOwnerCheckpoint = new Common.models.FinancialModels.BeneficialOwnerCheckpoint(
					checkpoint
				)
				break
			case 'Employment':
				this.employmentCheckpoint = new Common.models.ResumeModels.EmploymentCheckpoint(
					checkpoint
				)
				break
			case 'CurrentEmployment':
				this.currentEmploymentCheckpoint = new Common.models.ResumeModels.EmploymentCheckpoint(
					checkpoint
				)
				break
			case 'HighestEducation':
				this.highestEducationCheckpoint = new Common.models.ResumeModels.EducationCheckpoint(
					checkpoint
				)
				break
			case 'Educations':
				this.educationCheckpoint = new Common.models.ResumeModels.EducationCheckpoint(
					checkpoint
				)
				break
			case 'ReferencePerson':
				this.referencesCheckpoint = new Common.models.ResumeModels.ReferencesCheckpoint(
					checkpoint
				)
				break
			case 'OtherMerits':
				this.otherMeritsCheckpoint = new Common.models.ResumeModels.OtherMeritsCheckpoint(
					checkpoint
				)
				break
			case 'SoleProprietorship':
				this.soleproprietorshipCheckpoint = new Common.models.FinancialModels.SoleProprietorshipCheckpoint(
					checkpoint
				)
				break
			case 'InternetAndMedia':
				this.internetAndMediaCheckpoint = new Common.models.MediaModels.InternetAndMediaCheckpoint(
					checkpoint
				)
				break
			default:
				break
		}
	}

	@action changeLanguage() {
		const currentLanguage = this.order.language
		if (currentLanguage !== 'English') {
			this.order.language = 'English'
		}
		else {
			this.order.language = 'Svenska'
		}
	}

	@computed get showLanguageSwitch(): boolean {
		//Visa endast i förhandsgranskningen. Ej i de riktiga portalerna.
		return !this.userStore.user.companyId
	}

	@computed get hasCheckpointData(): boolean {
		return !this.order.isInternationalOrder && this.hasCollectedCheckpoints
	}
}
